import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { CardHeader, CardTitle, CardDescription } from '@/common/ui/card';
import { Bar } from 'react-chartjs-2';
import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Tooltip } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { PillarData } from '@/areas/culture-hub/types/api';
import { getChartTooltipConfig } from '@/common/components/charts/chartTooltipConfig';
import { MobilePageContent, MobilePageSection } from '@/mobile/layouts/common/MobilePageStyles';
import { Divider } from 'semantic-ui-react';

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

interface MobileScoresChartProps {
  pillars: PillarData[];
  teamId?: number | null;
  assessmentPeriodId?: number | null;
  teamName?: string | null;
}

export function computeChartData(pillars: PillarData[], teamLabel?: string, showDataLabels = true) {
  const teamData = pillars.map((p) => p.score);
  const orgData = pillars.map((p) => p.org_avg);
  const deltas = pillars.map((p) => p.score - p.org_avg);
  const labels = pillars.map((p) => p.name);
  const datasets: any[] = [];

  datasets.push({
    label: teamLabel || 'Team',
    data: teamData,
    backgroundColor: 'rgba(59, 130, 246, 0.9)',
    borderColor: 'rgba(59, 130, 246, 1)',
    borderWidth: 1,
    borderRadius: 6,
    barPercentage: showDataLabels ? 0.7 : 0.55,
    categoryPercentage: showDataLabels ? 0.8 : 0.7,
    datalabels: {
      display: showDataLabels,
      align: 'end',
      anchor: 'end',
      color: (context: any) => {
        const delta = deltas[context.dataIndex];
        return delta > 0 ? '#10b981' : delta < 0 ? '#ef4444' : '#6b7280';
      },
      font: {
        size: 10,
        weight: 'bold',
      },
      formatter: (_value: number, context: any) => {
        const delta = deltas[context.dataIndex];
        const deltaStr = delta > 0 ? `+${delta.toFixed(1)}` : delta.toFixed(1);
        return deltaStr;
      },
    },
  });

  datasets.push({
    label: 'Organization',
    data: orgData,
    backgroundColor: 'rgba(156, 163, 175, 0.6)',
    borderColor: 'rgba(156, 163, 175, 0.8)',
    borderWidth: 1,
    borderRadius: 6,
    barPercentage: showDataLabels ? 0.7 : 0.55,
    categoryPercentage: showDataLabels ? 0.8 : 0.7,
    datalabels: {
      display: showDataLabels,
      align: 'end',
      anchor: 'end',
      color: '#4b5563',
      font: {
        size: 10,
        weight: 600,
      },
      formatter: (value: number) => value.toFixed(1),
    },
  });

  return { labels, datasets, deltas };
}

const MobileScoresChart: React.FC<MobileScoresChartProps> = ({ pillars, teamId, assessmentPeriodId, teamName }) => {
  const isEmpty = !Array.isArray(pillars) || pillars.length === 0;
  const chartTitle = 'Team and Organization Scores';
  const chartDescription = pillars.length <= 1
    ? 'Detailed comparison for selected pillar'
    : 'Visual comparison across all culture pillars';

  const [isPortrait, setIsPortrait] = useState(true);

  useEffect(() => {
    const query = window.matchMedia('(orientation: portrait)');
    const update = () => setIsPortrait(query.matches);

    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  const { chartData, deltas } = useMemo(() => {
    const result = computeChartData(pillars, teamName ?? undefined, !isPortrait);
    return { chartData: { labels: result.labels, datasets: result.datasets }, deltas: result.deltas };
  }, [pillars, teamName, isPortrait]);

  const chartKey = useMemo(() => {
    const dataHash = pillars.map(p => `${p.name}:${p.score}:${p.org_avg}`).join('|');
    return `mobile-scores-chart-${assessmentPeriodId ?? 'no-period'}-${teamId ?? 'no-team'}-${dataHash}`;
  }, [pillars, teamId, assessmentPeriodId]);

  const yAxisMax = 10;

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          font: {
            size: 10,
            family: 'Poppins, sans-serif',
          },
          padding: 16,
          usePointStyle: true,
        },
      },
      tooltip: {
        ...getChartTooltipConfig(),
        callbacks: {
          afterLabel: (context: any) => {
            const dataIndex = context.dataIndex;
            const delta = deltas[dataIndex];
            if (delta !== undefined && delta !== null && !isNaN(delta)) {
              const deltaStr = delta > 0 ? `+${delta.toFixed(1)}` : delta.toFixed(1);
              const direction = delta > 0 ? '(above org)' : delta < 0 ? '(below org)' : '(same as org)';
              return `Delta: ${deltaStr} ${direction}`;
            }
            return '';
          },
        },
      },
      datalabels: {
        display: !isPortrait,
      },
    },
    scales: {
      x: {
        ticks: {
          font: {
            size: 11,
            family: 'Poppins, sans-serif',
          },
          color: '#4b5563',
          maxRotation: isPortrait ? 65 : 40,
          minRotation: isPortrait ? 55 : 0,
          autoSkip: true,
          maxTicksLimit: 6,
        },
        grid: {
          display: false,
        },
      },
      y: {
        min: 0,
        max: yAxisMax,
        ticks: {
          font: {
            size: 11,
            family: 'Poppins, sans-serif',
          },
          color: '#4b5563',
          stepSize: 1,
        },
        grid: {
          color: '#e5e7eb',
        },
      },
    },
    layout: {
      padding: {
        top: isPortrait ? 10 : 20,
        bottom: isPortrait ? 24 : 10,
      },
    },
  }), [deltas, isPortrait]);

  return (
    <MobilePageContent>
      <CardHeader>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}>
          <CardTitle className="text-title-section">{chartTitle}</CardTitle>
          <CardDescription className="text-description">{chartDescription}</CardDescription>
        </div>
      </CardHeader>

      <div style={{ display: 'grid', gap: '18px' }}>
        {isEmpty ? (
          <div data-testid="mobile-scores-chart-placeholder" className="text-center py-8 text-gray-500">
            No pillar data available to populate this chart. When team pillar scores are available this chart will show Team vs Organization comparisons.
          </div>
        ) : (
          <div style={{ position: 'relative', width: '100%', minHeight: isPortrait ? 320 : 320, maxHeight: isPortrait ? 420 : 420 }}>
            <Bar
              key={chartKey}
              data={chartData}
              options={options}
              plugins={[ChartDataLabels]}
              redraw
            />
          </div>
        )}
<Divider />
        {!isEmpty && (
          <MobilePageSection padtop="12px">
            <ComparedTitle>{(teamName || 'Team') + ' Compared to Organization'}</ComparedTitle>
            <ComparedGrid>
              {pillars.slice(0, 6).map((pillar, index) => {
                const delta = deltas[index];
                const deltaStr = delta > 0 ? `+${delta.toFixed(1)}` : delta.toFixed(1);
                return (
                  <ComparedChip key={pillar.key} positive={delta > 0} negative={delta < 0}>
                    <PillarName title={pillar.name}>{pillar.name}</PillarName>
                    <DeltaText style={{ color: delta > 0 ? '#047857' : delta < 0 ? '#b91c1c' : '#111827' }}>{deltaStr}</DeltaText>
                  </ComparedChip>
                );
              })}
              {pillars.length > 6 && (
                <ComparedChip key="more" positive={false} negative={false}>
                  <PillarName>+{pillars.length - 6} more</PillarName>
                </ComparedChip>
              )}
            </ComparedGrid>
          </MobilePageSection>
        )}
      </div>
    </MobilePageContent>
  );
};

const ComparedTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #111827;
  font-family: 'Poppins', sans-serif;
`;

const ComparedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
`;

const ComparedChip = styled.div<{ positive: boolean; negative: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  padding: 10px 12px;
  border-radius: 14px;
  border: 1px solid ${({ positive, negative }) => (positive ? '#86efac' : negative ? '#fecaca' : '#e5e7eb')};
  background-color: ${({ positive, negative }) => (positive ? '#ecfdf5' : negative ? '#fef2f2' : '#f8fafc')};
  text-align: center;
`;

const PillarName = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #475569;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
`;

const DeltaText = styled.div`
  font-size: 14px;
  font-weight: 700;
`;

export default MobileScoresChart;
